import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import logo from "../assets/WhatsApp Image 2024-12-03 at 3.41.47 PM.jpeg";
import { SITE, whatsappLink } from "../lib/site";
import { WhatsAppIcon } from "./WhatsAppButton";

const COLUMNS = [
  {
    title: "Admissions",
    links: [
      ["Admissions overview", "/admissions"],
      ["Apply online", "/admissions/signup"],
      ["Parent portal", "/admissions/login"],
      ["Admission process", "/Admission"],
      ["Fee structure", "/FeeStructure"],
      ["Scholarships", "/Scholarship"],
    ],
  },
  {
    title: "Academics",
    links: [
      ["Primary Years (PYP)", "/PrimaryYearsProgram"],
      ["Middle Years (MYP)", "/MYP"],
      ["IB Diploma (DP)", "/IBDiploma"],
      ["Matriculation division", "/matriculation"],
      ["Career counselling", "/CareerAndUniCounseling"],
    ],
  },
  {
    title: "School",
    links: [
      ["TIS history", "/Tishistory"],
      ["Head's message", "/HeadsMessage"],
      ["Gallery", "/gallery"],
      ["Alumni", "/alumni"],
      ["Job vacancies", "/JobVacancies"],
      ["Contact us", "/ContactDetails"],
    ],
  },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-navy-950 text-navy-100">
      <div className="border-b border-white/10 bg-navy-800">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-5 px-4 py-8 sm:px-6 md:flex-row md:items-center">
          <div>
            <p className="font-display text-2xl font-extrabold text-white">Admissions are open</p>
            <p className="mt-1 text-navy-100">IB Continuum and Matriculation. Apply online in about 10 minutes.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/admissions" className="rounded-xl bg-gold-400 px-5 py-3 text-sm font-bold text-navy-900 hover:bg-gold-300">
              Start your application
            </Link>
            <a
              href={whatsappLink("Assalam o Alaikum, I would like information about admissions at TIS.")}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 text-sm font-bold text-white hover:bg-[#1ebe5a]"
            >
              <WhatsAppIcon className="h-5 w-5" /> WhatsApp us
            </a>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.4fr_repeat(3,1fr)]">
        <div>
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="" className="h-14 w-14 rounded-full bg-white object-contain p-1" />
            <span className="font-display text-lg font-extrabold leading-tight text-white">
              The International School
              <span className="block text-xs font-semibold text-gold-400">{SITE.tagline}</span>
            </span>
          </Link>
          <ul className="mt-6 space-y-3 text-sm">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" aria-hidden />
              <a href={SITE.mapUrl} target="_blank" rel="noreferrer" className="hover:text-white">
                {SITE.address}
              </a>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" aria-hidden />
              <span>
                UAN{" "}
                <a href={`tel:${SITE.uanTel}`} className="whitespace-nowrap font-semibold text-white hover:underline">
                  {SITE.uan}
                </a>{" "}
                &middot;{" "}
                <a href={`tel:${SITE.telHref}`} className="whitespace-nowrap font-semibold text-white hover:underline">
                  {SITE.tel}
                </a>{" "}
                &middot;{" "}
                <a href={`tel:${SITE.phoneTel}`} className="whitespace-nowrap font-semibold text-white hover:underline">
                  {SITE.phone}
                </a>
              </span>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold-400" aria-hidden />
              <a href={`mailto:${SITE.email}`} className="font-semibold text-white hover:underline">
                {SITE.email}
              </a>
            </li>
          </ul>
          <div className="mt-6 flex gap-3">
            {[
              [FaFacebookF, SITE.socials.facebook, "Facebook"],
              [FaInstagram, SITE.socials.instagram, "Instagram"],
              [FaLinkedinIn, SITE.socials.linkedin, "LinkedIn"],
            ].map(([Icon, href, label]) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-gold-400 hover:text-navy-900"
              >
                <Icon />
              </a>
            ))}
          </div>
        </div>
        {COLUMNS.map((col) => (
          <div key={col.title}>
            <p className="font-display text-sm font-bold uppercase tracking-wider text-white">{col.title}</p>
            <ul className="mt-4 space-y-2.5 text-sm">
              {col.links.map(([label, to]) => (
                <li key={to}>
                  <Link to={to} className="hover:text-gold-300">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-xs text-navy-200 sm:flex-row sm:px-6">
          <p>&copy; {year} The International School. All rights reserved.</p>
          <p className="flex gap-4">
            <a href={SITE.manageBac} target="_blank" rel="noreferrer" className="hover:text-white">
              ManageBac
            </a>
            <a href="/admin/" className="hover:text-white">
              Staff login
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
